import { Link, useNavigate } from "react-router-dom";
import { useAuth, useCart } from "../App";
import { ShoppingBag, Heart, User, LogOut, ChevronDown, Menu, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../App";
import SearchBar from "./SearchBar";

const CATS_FALLBACK = [
  { slug: "rings", name: "Rings" },
  { slug: "earrings", name: "Earrings" },
  { slug: "necklaces", name: "Necklaces" },
  { slug: "bangles", name: "Bangles" },
  { slug: "mangalsutra", name: "Mangalsutra" },
  { slug: "pendants", name: "Pendants" },
  { slug: "silver", name: "Silver" },
];

const BUDGETS = [
  { label: "Under ₹5,000", min: 0, max: 5000, key: "u5k" },
  { label: "₹5,000 – ₹10,000", min: 5000, max: 10000, key: "5-10" },
  { label: "₹10,000 – ₹20,000", min: 10000, max: 20000, key: "10-20" },
  { label: "₹20,000 – ₹50,000", min: 20000, max: 50000, key: "20-50" },
  { label: "₹50,000 – ₹1,00,000", min: 50000, max: 100000, key: "50-100" },
  { label: "Above ₹1,00,000", min: 100000, max: 10000000, key: "1L+" },
];

function AllProductsMega({ navigate, categories }) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const closeTimer = useRef(null);

  useEffect(() => {
    // Load a lightweight preview of latest products for the mega panel
    axios.get(`${API}/products?limit=60`)
      .then(r => setProducts(r.data?.items || []))
      .catch(() => setProducts([]));
  }, []);

  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon} data-testid="nav-all-products-wrapper">
      <button
        onClick={() => go("/products")}
        onFocus={openNow}
        className={`inline-flex items-center gap-1 text-sm font-semibold transition-colors ${
          open ? "text-[#8B1E28]" : "text-[#2C2A29] hover:text-[#8B1E28]"
        }`}
        data-testid="nav-all-products"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Sparkles className={`h-3.5 w-3.5 transition-colors ${open ? "text-[#8B1E28]" : "text-[#C5A059]"}`} />
        All Products
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Mega panel — anchored to left of the trigger so it doesn't overflow off-screen */}
      <div
        className={`absolute left-0 top-full pt-3 w-[min(920px,92vw)] z-40 transition-all duration-200 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        data-testid="mega-all-products"
      >
        <div className="rounded-3xl bg-white border border-[#EAE3D9] shadow-[0_30px_80px_-20px_rgba(139,30,40,0.25)] overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-[minmax(240px,280px)_1fr]">
            {/* COL 1: Budget & category quick links */}
            <div className="bg-[#FDFBF7] p-5 border-b md:border-b-0 md:border-r border-[#EAE3D9]">
              <div className="label-small text-[#8A8480] mb-3">Shop by Price</div>
              <ul className="space-y-1.5">
                {BUDGETS.map(b => (
                  <li key={b.key}>
                    <button
                      onClick={() => go(`/products?min_price=${b.min}&max_price=${b.max}`)}
                      className="group w-full flex items-center justify-between text-left px-3 py-2 rounded-lg text-sm text-[#2C2A29] hover:bg-white hover:shadow-sm hover:text-[#8B1E28] transition-all"
                      data-testid={`mega-budget-${b.key}`}
                    >
                      <span>{b.label}</span>
                      <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </button>
                  </li>
                ))}
              </ul>

              <div className="mt-5 pt-5 border-t border-[#EAE3D9]">
                <div className="label-small text-[#8A8480] mb-3">Shop by Category</div>
                <ul className="grid grid-cols-2 gap-1">
                  {categories.map(c => (
                    <li key={c.slug}>
                      <button
                        onClick={() => go(`/products/${c.slug}`)}
                        className="w-full text-left px-2.5 py-1.5 rounded-md text-xs text-[#595552] hover:text-[#8B1E28] hover:bg-white transition-colors"
                        data-testid={`mega-cat-${c.slug}`}
                      >
                        {c.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* COL 2: Product thumbnails */}
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="label-small text-[#8A8480]">The Catalogue</div>
                <button
                  onClick={() => go("/products")}
                  className="text-xs text-[#8B1E28] hover:underline inline-flex items-center gap-1"
                  data-testid="mega-view-all"
                >
                  View all <ArrowRight className="h-3 w-3" />
                </button>
              </div>

              {products.length === 0 ? (
                <div className="text-sm text-[#8A8480] py-10 text-center">
                  No products yet. Add pieces from the Admin panel.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[380px] overflow-y-auto pr-1 mega-scroll">
                  {products.slice(0, 12).map(p => (
                    <button
                      key={p.product_id}
                      onClick={() => go(`/product/${p.product_id}`)}
                      className="group text-left rounded-xl overflow-hidden border border-transparent hover:border-[#C5A059] hover:shadow-md transition-all"
                      data-testid={`mega-product-${p.product_id}`}
                    >
                      <div className="aspect-square bg-[#FFF5F3] overflow-hidden">
                        <img
                          src={p.images?.[0]}
                          alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-2">
                        <div className="text-xs font-medium text-[#2C2A29] line-clamp-1">{p.name}</div>
                        <div className="text-[11px] text-[#8B1E28] font-semibold mt-0.5">
                          ₹{Number(p.price).toLocaleString("en-IN")}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const { user, login, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categories, setCategories] = useState(CATS_FALLBACK);
  const itemsCount = cart?.items?.reduce((s, i) => s + i.quantity, 0) || 0;

  useEffect(() => {
    axios.get(`${API}/categories`)
      .then(r => { if (r.data?.length) setCategories(r.data); })
      .catch(() => {});
  }, []);

  const topCats = categories.slice(0, 7);
  const moreCats = categories.slice(7);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/90 border-b border-[#EAE3D9]" data-testid="site-header">
      {/* Top strip */}
      <div className="bg-[#8B1E28] text-[#FFF5F3] text-xs">
        <div className="overflow-hidden">
          <div className="marquee-track py-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex gap-12 whitespace-nowrap label-small">
                <span>✦ Hand-crafted in India</span>
                <span>✦ BIS Hallmarked 18k & 22k</span>
                <span>✦ Free shipping above ₹2,500</span>
                <span>✦ Lifetime exchange</span>
                <span>✦ Made with love by Sumanya</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Row 1: Logo (hero) + Icons */}
        <div className="flex items-center justify-between gap-3 py-3 sm:py-4">
          <button className="lg:hidden shrink-0" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle" aria-label="Open menu">
            <Menu className="h-6 w-6 text-[#2C2A29]" />
          </button>

          <Link to="/" className="flex items-center gap-2 shrink-0 mx-auto lg:mx-0" data-testid="logo-link" aria-label="Sumanya home">
            <img
              src="https://customer-assets.emergentagent.com/job_sumanya-collection/artifacts/v848dqa8_Sumanya_logo_hd-removebg-preview.png"
              alt="Sumanya — A brand by Suman Jewellers"
              className="h-14 sm:h-16 lg:h-24 w-auto drop-shadow-sm"
            />
          </Link>

          {/* Desktop search sits inline on ≥lg — big but never crowds the nav row. */}
          <div className="hidden lg:flex flex-1 justify-center px-4 xl:px-8 min-w-0">
            <div className="w-full max-w-2xl"><SearchBar variant="desktop" /></div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 shrink-0">
            <button onClick={() => user ? navigate("/wishlist") : login()} className="hover:text-[#8B1E28] transition-colors hidden sm:block" data-testid="wishlist-btn" aria-label="Wishlist">
              <Heart className="h-5 w-5" />
            </button>
            <button onClick={() => navigate("/cart")} className="relative hover:text-[#8B1E28] transition-colors" data-testid="cart-btn" aria-label="Cart">
              <ShoppingBag className="h-5 w-5" />
              {itemsCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#8B1E28] text-white text-[10px] font-semibold rounded-full h-4 w-4 flex items-center justify-center" data-testid="cart-count">
                  {itemsCount}
                </span>
              )}
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2" data-testid="user-menu">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full border-2 border-[#C5A059]" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-[#8B1E28] text-white flex items-center justify-center text-sm">
                      {user.name?.[0] || "U"}
                    </div>
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                    <User className="h-4 w-4 mr-2" /> Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                    <ShoppingBag className="h-4 w-4 mr-2" /> My Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/wishlist")}>
                    <Heart className="h-4 w-4 mr-2" /> Wishlist
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                        Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button onClick={login} className="btn-primary text-xs sm:text-sm px-3 sm:px-4 py-2 h-auto whitespace-nowrap" data-testid="login-btn">
                Sign In
              </Button>
            )}
          </div>
        </div>

        {/* Row 2: primary nav (desktop only) */}
        <nav className="hidden lg:flex items-center justify-center gap-6 border-t border-[#EAE3D9]/60 py-3" data-testid="primary-nav">
          <AllProductsMega navigate={navigate} categories={categories} />
          {topCats.map(c => (
            <Link key={c.slug} to={`/products/${c.slug}`}
              className="text-sm font-medium text-[#2C2A29] hover:text-[#8B1E28] transition-colors whitespace-nowrap"
              data-testid={`nav-${c.slug}`}>
              {c.name}
            </Link>
          ))}
          {moreCats.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-[#2C2A29] hover:text-[#8B1E28] whitespace-nowrap" data-testid="nav-more">
                More <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {moreCats.map(c => (
                  <DropdownMenuItem key={c.slug} onClick={() => navigate(`/products/${c.slug}`)} data-testid={`nav-more-${c.slug}`}>
                    {c.name}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/products")} data-testid="nav-shop-all">Shop All</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </nav>

        {/* Mobile/tablet search row — always visible so search is one-tap everywhere */}
        <div className="lg:hidden pb-3">
          <SearchBar variant="mobile" />
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-[#EAE3D9] py-4" data-testid="mobile-menu">
            <div className="mb-4">
              <div className="label-small text-[#8A8480] mb-2">Shop by Price</div>
              <div className="grid grid-cols-2 gap-2">
                {BUDGETS.map(b => (
                  <button
                    key={b.key}
                    onClick={() => { navigate(`/products?min_price=${b.min}&max_price=${b.max}`); setMobileOpen(false); }}
                    className="px-3 py-2 rounded-lg text-xs border border-[#EAE3D9] text-[#2C2A29] hover:border-[#C5A059] hover:text-[#8B1E28] transition-colors"
                    data-testid={`mobile-budget-${b.key}`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="label-small text-[#8A8480] mb-2">Shop by Category</div>
            <div className="grid grid-cols-2 gap-2">
              <Link to="/products" className="py-2 text-sm font-semibold text-[#8B1E28]" onClick={() => setMobileOpen(false)} data-testid="mobile-all-products">
                All Products
              </Link>
              {categories.map(c => (
                <Link key={c.slug} to={`/products/${c.slug}`} className="py-2 text-sm" onClick={() => setMobileOpen(false)}>
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
